export type Handler<T> = (payload: T) => void
export type Filter<T> = (payload: T) => boolean

export interface Subscription<T> {
    handler: Handler<T>
    filter?: Filter<T>
    once?: boolean
}

/**
 * Handlers basic pub and sub using the handlers object id to remove event listener.
 * Similar to EventTarget.
 */
export class Subscriber<T> {
    private subscriptions: Array<Subscription<T>> = []

    public subscribe(handler: Handler<T>, filter?: Filter<T>, once?: boolean) {
        this.subscriptions.push({ handler, filter, once })
    }

    public unsubscribe(handler: Handler<T>, onError: (error: Error) => void) {
        const index = this.subscriptions.findIndex((subscription) => subscription.handler === handler)
        if (index === -1) {
            onError(new Error('Tried to unsubscribe, but could not find subscription'))
        } else {
            this.subscriptions.splice(index, 1)
        }
    }

    public dispatch(payload: T, onError: (error: Error) => void) {
        for (const subscription of this.subscriptions) {
            try {
                if (subscription.filter != null && !subscription.filter(payload)) return
                subscription.handler(payload)

                if (subscription.once) {
                    this.unsubscribe(subscription.handler, onError)
                }
            } catch (error) {
                onError(error)
            }
        }
    }

    public async waitForNext(filter?: Filter<T>): Promise<T> {
        return new Promise<T>((resolve) => this.subscribe(resolve, filter, true))
    }
}
